import '../styles/Login.css';
import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from '../context/AuthContext';
import WheelLoader from '../components/WheelLoader';
import BackButton from '../components/BackButton';

const EditPost = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, token, isAuthenticated, loading } = useAuth();
  
  const [dataLoading, setDataLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [marcas, setMarcas] = useState([]);
  const [categorias, setCategorias] = useState([]);
  
  // Estados del formulario
  const [formData, setFormData] = useState({
    titulo: '',
    descripcion_corta: '',
    descripcion: '',
    detalle: '',
    url: '',
    year_vehiculo: new Date().getFullYear(),
    id_categoria_vehiculo: '',
    id_marca_vehiculo: ''
  });
  
  // Estados para imágenes - ACTUALIZADOS
  const [currentImages, setCurrentImages] = useState([]);
  const [newImages, setNewImages] = useState([]);
  const [imagesToKeep, setImagesToKeep] = useState(new Set());
  const [previewUrls, setPreviewUrls] = useState([]);
  const [imageOrder, setImageOrder] = useState([]); // NUEVO: orden de todas las imágenes

  const API_URL = (process.env.REACT_APP_API_URL || '').replace(/^http:/, 'https:');

  // FUNCIONES DE REORDENAMIENTO
  const moveImageUp = (index) => {
    if (index > 0) {
      const newOrder = [...imageOrder];
      [newOrder[index - 1], newOrder[index]] = [newOrder[index], newOrder[index - 1]];
      setImageOrder(newOrder);
    }
  };

  const moveImageDown = (index) => {
    if (index < imageOrder.length - 1) {
      const newOrder = [...imageOrder];
      [newOrder[index], newOrder[index + 1]] = [newOrder[index + 1], newOrder[index]];
      setImageOrder(newOrder);
    }
  };

  const getPortadaId = () => {
    if (imageOrder.length === 0) return null;
    const firstImage = imageOrder[0];
    return firstImage.type === 'existing' ? firstImage.id : `nueva_${firstImage.index}`;
  };

  const willBePortada = (image, index) => {
    return index === 0;
  };

  // Cargar datos de la publicación
  useEffect(() => {
    if (loading) return;
    if (!isAuthenticated) {
      navigate('/iniciarsesion');
      return;
    }
    if (!user) return;

    const fetchData = async () => {
      try {
        const postRes = await fetch(`${API_URL}/api/v1/publicacion/edit-post/${id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        const postData = await postRes.json();
        
        if (!postRes.ok) {
          throw new Error('Publicación no encontrada');
        }

        if (user.id !== postData.id_usuario) {
          alert('No tienes permiso para editar esta publicación');
          navigate('/');
          return;
        }

        const [marcasRes, categoriasRes] = await Promise.all([
          fetch(`${API_URL}/api/v1/marca/`),
          fetch(`${API_URL}/api/v1/categoria/`)
        ]);

        const marcasData = await marcasRes.json();
        const categoriasData = await categoriasRes.json();

        setFormData({
          titulo: postData.titulo || '',
          descripcion_corta: postData.descripcion_corta || '',
          descripcion: postData.descripcion || '',
          detalle: postData.detalle || '',
          url: postData.url || '',
          year_vehiculo: postData.year_vehiculo || new Date().getFullYear(),
          id_categoria_vehiculo: postData.id_categoria_vehiculo || '',
          id_marca_vehiculo: postData.id_marca_vehiculo || ''
        });

        setMarcas(marcasData);
        setCategorias(categoriasData);
        
        if (postData.imagenes && postData.imagenes.length > 0) {
          const imagesWithIds = postData.imagenes
            .sort((a, b) => (a.numero_imagen || 0) - (b.numero_imagen || 0))
            .map(img => ({
              id: img.id_imagen,
              url: img.url_foto,
              is_portada: img.numero_imagen === 1, // CAMBIO: basado en numero_imagen
              numero_imagen: img.numero_imagen || 0
            }));
          
          setCurrentImages(imagesWithIds);
          setImagesToKeep(new Set(imagesWithIds.map(img => img.id)));
          
          // Inicializar orden con imágenes existentes
          const initialOrder = imagesWithIds.map(img => ({
            type: 'existing',
            id: img.id,
            url: img.url,
            is_portada: img.numero_imagen === 1, // CAMBIO: basado en numero_imagen
            numero_imagen: img.numero_imagen
          }));
          setImageOrder(initialOrder);
        }

      } catch (error) {
        console.error('Error cargando datos:', error);
        alert('Error cargando la publicación');
        navigate('/');
      } finally {
        setDataLoading(false);
      }
    };

    fetchData();
  }, [id, API_URL, user, isAuthenticated, loading, navigate]);

  useEffect(() => {
    return () => {
      previewUrls.forEach(url => URL.revokeObjectURL(url));
    };
  }, [previewUrls]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageToggle = (imageId) => {
    setImagesToKeep(prev => {
      const newSet = new Set(prev);
      if (newSet.has(imageId)) {
        newSet.delete(imageId);
        // Remover de imageOrder también
        setImageOrder(currentOrder => 
          currentOrder.filter(img => !(img.type === 'existing' && img.id === imageId))
        );
      } else {
        newSet.add(imageId);
        // Añadir de vuelta a imageOrder en su posición original
        const originalImage = currentImages.find(img => img.id === imageId);
        if (originalImage) {
          const orderItem = {
            type: 'existing',
            id: originalImage.id,
            url: originalImage.url,
            is_portada: originalImage.numero_imagen === 1, // CAMBIO: basado en numero_imagen
            numero_imagen: originalImage.numero_imagen
          };
          setImageOrder(currentOrder => {
            const newOrder = [...currentOrder, orderItem];
            return newOrder.sort((a, b) => {
              if (a.type === 'existing' && b.type === 'existing') {
                return (a.numero_imagen || 0) - (b.numero_imagen || 0);
              }
              if (a.type === 'existing' && b.type === 'new') return -1;
              if (a.type === 'new' && b.type === 'existing') return 1;
              return 0;
            });
          });
        }
      }
      return newSet;
    });
  };

  const handleNewImagesChange = (e) => {
    const files = Array.from(e.target.files);
    setNewImages(files);
    
    // Revocar URLs anteriores
    previewUrls.forEach(url => URL.revokeObjectURL(url));
    
    const urls = files.map(file => URL.createObjectURL(file));
    setPreviewUrls(urls);

    // Actualizar imageOrder con nuevas imágenes
    const newImageItems = files.map((file, index) => ({
      type: 'new',
      index,
      file,
      url: urls[index]
    }));

    setImageOrder(currentOrder => [
      ...currentOrder.filter(img => img.type === 'existing'),
      ...newImageItems
    ]);
  };

  const removeNewImage = (index) => {
    const newFiles = newImages.filter((_, i) => i !== index);
    const newUrls = previewUrls.filter((_, i) => i !== index);
    
    URL.revokeObjectURL(previewUrls[index]);
    
    setNewImages(newFiles);
    setPreviewUrls(newUrls);

    // Actualizar imageOrder y reindexar
    const updatedOrder = imageOrder
      .filter(img => !(img.type === 'new' && img.index === index))
      .map(img => {
        if (img.type === 'new' && img.index > index) {
          return { ...img, index: img.index - 1, url: previewUrls[img.index - 1] };
        }
        return img;
      });

    setImageOrder(updatedOrder);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.titulo.trim()) {
      alert('El título es obligatorio');
      return;
    }

    if (imageOrder.length === 0) {
      alert('Debe mantener al menos una imagen o agregar nuevas imágenes');
      return;
    }

    setSaving(true);

    try {
      const formDataToSend = new FormData();
      
      Object.keys(formData).forEach(key => {
        formDataToSend.append(key, formData[key]);
      });

      // Solo enviar IDs de imágenes que se mantienen
      const keepIds = imageOrder
        .filter(img => img.type === 'existing')
        .map(img => img.id)
        .join(',');
      formDataToSend.append('mantener_imagenes', keepIds);

      // Enviar información sobre portada (la primera imagen en el orden)
      const portadaId = getPortadaId();
      if (portadaId) {
        formDataToSend.append('nueva_portada', portadaId);
      }

      // Enviar nuevas imágenes en el orden correcto
      const newImagesInOrder = imageOrder
        .filter(img => img.type === 'new')
        .map(img => newImages[img.index]);
      
      newImagesInOrder.forEach(file => {
        formDataToSend.append('files', file);
      });

      const response = await fetch(`${API_URL}/api/v1/publicacion/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formDataToSend
      });

      if (response.ok) {
        const result = await response.json();
        console.log('Publicación actualizada:', result);
        alert('¡Publicación actualizada exitosamente!');
        navigate(`/post/${id}`);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Error al actualizar la publicación');
      }

    } catch (error) {
      console.error('Error actualizando publicación:', error);
      alert(`Error: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  if (loading || dataLoading) {
    return <div><WheelLoader /></div>;
  }

  return (
    <div className="login-container">
      <BackButton/>
      <div className="login-logo">
        <h1>Editar Publicación</h1>
        <p>Modifica los datos de tu publicación</p>
      </div>
      
      <form onSubmit={handleSubmit}>
        {/* Imágenes actuales y nuevas */}
        <div className="form-group">
          <label>Imágenes</label>
          
          {imageOrder.length > 0 && (
            <div className="file-preview">
              <div style={{ 
                fontSize: '0.9rem', 
                color: '#374151', 
                marginBottom: '15px',
                padding: '10px',
                backgroundColor: '#f9fafb',
                borderRadius: '6px',
                border: '1px solid #e5e7eb'
              }}>
              <div className="preview-grid">
                {imageOrder.map((image, idx) => (
                  <div className="preview-item" key={`${image.type}-${image.id || image.index}`} style={{ position: 'relative' }}>
                    <img 
                      src={image.type === 'existing' ? image.url : image.url} 
                      alt={`Imagen ${idx + 1}`}
                      style={{
                        opacity: image.type === 'existing' && !imagesToKeep.has(image.id) ? 0.5 : 1
                      }}
                    />
                    
                    {/* Número de imagen (RESETEO AUTOMÁTICO) */}
                    <div style={{
                      position: 'absolute',
                      top: '5px',
                      left: '5px',
                      backgroundColor: willBePortada(image, idx) ? '#ef4444' : '#3b82f6',
                      color: 'white',
                      borderRadius: '50%',
                      width: '24px',
                      height: '24px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      zIndex: 2
                    }}>
                      {idx + 1}
                    </div>

                    {/* Etiqueta de portada */}
                    {willBePortada(image, idx) && (
                      <div style={{
                        position: 'absolute',
                        top: '5px',
                        right: '30px',
                        backgroundColor: '#ef4444',
                        color: 'white',
                        padding: '2px 6px',
                        borderRadius: '4px',
                        fontSize: '10px',
                        fontWeight: 'bold',
                        zIndex: 2
                      }}>
                        PORTADA
                      </div>
                    )}

                    {/* Controles de reordenamiento */}
                    <div style={{
                      position: 'absolute',
                      top: '5px',
                      right: '5px',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: '2px',
                      zIndex: 2
                    }}>
                      {idx > 0 && (
                        <button
                          type="button"
                          onClick={() => moveImageUp(idx)}
                          style={{
                            width: '20px',
                            height: '20px',
                            borderRadius: '4px',
                            backgroundColor: 'rgba(59, 130, 246, 0.9)',
                            color: 'white',
                            border: 'none',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '12px'
                          }}
                          title="Mover hacia arriba"
                        >
                          ↑
                        </button>
                      )}
                      
                      {idx < imageOrder.length - 1 && (
                        <button
                          type="button"
                          onClick={() => moveImageDown(idx)}
                          style={{
                            width: '20px',
                            height: '20px',
                            borderRadius: '4px',
                            backgroundColor: 'rgba(59, 130, 246, 0.9)',
                            color: 'white',
                            border: 'none',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '12px'
                          }}
                          title="Mover hacia abajo"
                        >
                          ↓
                        </button>
                      )}
                    </div>

                    {/* Controles específicos por tipo de imagen */}
                    {image.type === 'existing' ? (
                      // Checkbox para mantener imagen existente
                      <div style={{
                        position: 'absolute',
                        bottom: '5px',
                        left: '5px',
                        zIndex: 2
                      }}>
                        <input
                          type="checkbox"
                          checked={imagesToKeep.has(image.id)}
                          onChange={() => handleImageToggle(image.id)}
                          style={{
                            width: '18px',
                            height: '18px',
                            cursor: 'pointer'
                          }}
                        />
                      </div>
                    ) : (
                      // Botón eliminar para imagen nueva
                      <button 
                        type="button" 
                        onClick={() => removeNewImage(image.index)}
                        style={{
                          position: 'absolute',
                          bottom: '5px',
                          right: '5px',
                          width: '24px',
                          height: '24px',
                          borderRadius: '50%',
                          backgroundColor: 'rgba(239, 68, 68, 0.9)',
                          color: 'white',
                          border: 'none',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '16px',
                          fontWeight: 'bold',
                          zIndex: 2
                        }}
                        title="Eliminar imagen"
                      >
                        ×
                      </button>
                    )}
                  </div>
                ))}
              </div>
            </div>
            </div>
          )}

          {/* Input para agregar nuevas imágenes */}
          <div style={{ marginTop: '15px' }}>
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={handleNewImagesChange}
              style={{ marginBottom: '10px' }}
            />
            <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>
              Agregar nuevas imágenes (se añadirán al final)
            </div>
          </div>
        </div>

        {/* Campos del formulario */}
        <div className="form-group">
          <label>Título *</label>
          <textarea
            name="titulo"
            value={formData.titulo}
            onChange={handleInputChange}
            maxLength={50}
            required
          />
        </div>

        <div className="form-group">
          <label>Descripción corta *</label>
          <textarea
            name="descripcion_corta"
            value={formData.descripcion_corta}
            onChange={handleInputChange}
            maxLength={150}
            required
          />
          <div className={`char-counter ${formData.descripcion_corta.length > 135 ? 'error' : formData.descripcion_corta.length > 120 ? 'warning' : ''}`}>
            {formData.descripcion_corta.length}/150
          </div>
        </div>

        <div className="form-group">
          <label>Descripción detallada *</label>
          <textarea
            className="long-description"
            name="descripcion"
            value={formData.descripcion}
            onChange={handleInputChange}
            maxLength={5000}
            required
          />
          <div className={`char-counter ${formData.descripcion.length > 5000 ? 'error' : formData.descripcion.length > 4500 ? 'warning' : ''}`}>
            {formData.descripcion.length}/5000
          </div>
        </div>

        <div className="form-group">
          <label>Características *</label>
          <textarea
            className="long-description"
            name="detalle"
            value={formData.detalle}
            onChange={handleInputChange}
            maxLength={2000}
            required
          />
          <div className={`char-counter ${formData.detalle.length > 2000 ? 'error' : formData.detalle.length > 1800 ? 'warning' : ''}`}>
            {formData.detalle.length}/2000
          </div>
        </div>

        <div className="form-group">
          <label>Año del vehículo *</label>
          <input
            type="number"
            name="year_vehiculo"
            value={formData.year_vehiculo}
            onChange={handleInputChange}
            required
          />
        </div>

        <div className="form-group">
          <label>Categoría *</label>
          <select
            name="id_categoria_vehiculo"
            value={formData.id_categoria_vehiculo}
            onChange={handleInputChange}
            required
          >
            <option value="">Seleccione una categoría</option>
            {categorias.map(cat => (
              <option key={cat.id_categoria_vehiculo} value={cat.id_categoria_vehiculo}>
                {cat.nombre_categoria_vehiculo}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>Marca *</label>
          <select
            name="id_marca_vehiculo"
            value={formData.id_marca_vehiculo}
            onChange={handleInputChange}
            required
          >
            <option value="">Seleccione una marca</option>
            {marcas.map(m => (
              <option key={m.id_marca_vehiculo} value={m.id_marca_vehiculo}>
                {m.nombre_marca_vehiculo}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>Enlace opcional</label>
          <input
            type="url"
            name="url"
            value={formData.url}
            onChange={handleInputChange}
            placeholder="https://ejemplo.com (opcional)"
          />
        </div>
        
        <div className="button-group">
          <button type="submit" className="publish-btn" disabled={saving || imageOrder.length === 0}>
            {saving ? 'Guardando...' : 'Guardar Cambios'}
          </button>
          <button type="button" className="cancel-btn" onClick={() => navigate(`/post/${id}`)}>
            Cancelar
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditPost;
